/**
 * 机上調査テンプレート マスタ画面 (docs/27 §17-4、master のみ)。
 *
 * 机上調査 (ストリートビュー / 360度写真) の解析の設定と文面・調査項目の既定・結果の読み取りに使う項目を
 * テンプレートとして管理し、地区に適用する (地区の机上調査ページで選ぶ)。地区はテンプレートの値を項目ごとに
 * 上書きできる。解析で使う出力フォーマットは地区の実効の設定で決まり、ここで選ぶ出力フォーマットは
 * 項目の候補とプロンプトのプレビューにだけ使う。
 */
import { Button, Card, useToast } from '@citizen-walk/shared-ui';
import {
  resolveStreetSurveyExtraction,
  STREET_SURVEY_TEMPLATES_COLLECTION,
  type AiCategoryMaster,
  type StreetSurveyDisplayDef,
  type StreetSurveyTemplate,
} from '@citizen-walk/shared-types';
import { useMemo, useState } from 'react';
import { useAdminAuth } from '../auth';
import { useDistricts } from '../districts-admin';
import {
  extractionDefFromDraft,
  extractionDraftFromDef,
  resultFieldsDefFromDraft,
  resultFieldsDraftFromDef,
  StreetSurveyAxisDefaultsForm,
  StreetSurveyExtractionForm,
  StreetSurveyPromptPreview,
  StreetSurveyResultFieldsForm,
  type StreetSurveyAxisDefaults,
  type StreetSurveyExtractionDraft,
  type StreetSurveyResultFieldsDraft,
} from '../street-survey-settings';
import { DOCUMENT_ID_PATTERN } from './aiAnalysisEditorHelpers';
import { stripSystemFields } from './jsonImportExport';
import { MasterHistoryView } from './MasterHistoryView';
import { MasterImportExportButtons, RowExportButton } from './MasterImportExportButtons';
import {
  archiveMaster,
  upsertMaster,
  useAiCategoryMasters,
  useAiOutputFormats,
  useStreetSurveyTemplates,
} from './useMasters';

type TemplateItem = StreetSurveyTemplate & { id: string };

interface Draft {
  id: string;
  displayName: string;
  description: string;
  referenceOutputFormatId: string;
  axes: StreetSurveyAxisDefaults;
  /** 画面では編集しないが保存時に残す。 */
  listFieldKeys: string[];
  extraction: StreetSurveyExtractionDraft;
  result: StreetSurveyResultFieldsDraft;
}

function draftFromItem(item: TemplateItem | null): Draft {
  const display = item?.display ?? {};
  return {
    id: item?.id ?? '',
    displayName: item?.displayName ?? '',
    description: item?.description ?? '',
    referenceOutputFormatId: item?.referenceOutputFormatId ?? '',
    axes: {
      legendAxisKey: display.legendAxisKey ?? null,
      aggregateAxisKeys: display.aggregateAxisKeys ?? [],
      evaluationField: display.evaluationField ?? null,
    },
    listFieldKeys: display.listFieldKeys ?? [],
    extraction: extractionDraftFromDef(display.extraction),
    result: resultFieldsDraftFromDef(display.result),
  };
}

/** 取り込む JSON の最低限の形 (ID・表示名・設定のオブジェクト)。 */
export function validateStreetSurveyTemplate(raw: unknown): TemplateItem | null {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return null;
  const r = raw as Record<string, unknown>;
  if (typeof r.id !== 'string' || !DOCUMENT_ID_PATTERN.test(r.id)) return null;
  if (typeof r.displayName !== 'string' || !r.displayName.trim()) return null;
  if (!r.display || typeof r.display !== 'object' || Array.isArray(r.display)) return null;
  return r as unknown as TemplateItem;
}

const inputClass = 'mt-0.5 block w-full rounded border border-slate-300 px-2 py-1 text-sm bg-white';

export function StreetSurveyTemplatesPage() {
  const { state: authState } = useAdminAuth();
  const uid = authState.user?.uid ?? 'admin-stub';
  const { toast } = useToast();
  const state = useStreetSurveyTemplates({ includeArchived: true });
  const formatsState = useAiOutputFormats();
  const categoryState = useAiCategoryMasters();
  const districtsState = useDistricts();

  const [editing, setEditing] = useState<Draft | null>(null);
  const [isNew, setIsNew] = useState(false);
  const [saving, setSaving] = useState(false);
  const [historyTarget, setHistoryTarget] = useState<{ id: string; name: string } | null>(null);

  const allItems = useMemo(() => (state.status === 'ready' ? state.items : []), [state]);
  const activeItems = useMemo(() => allItems.filter((it) => !it.archivedAt), [allItems]);
  const formats = useMemo(
    () => (formatsState.status === 'ready' ? formatsState.items : []),
    [formatsState],
  );
  const mastersById = useMemo(
    () =>
      new Map<string, AiCategoryMaster>(
        (categoryState.status === 'ready' ? categoryState.items : []).map((m) => [m.id, m]),
      ),
    [categoryState],
  );
  /** テンプレート ID → 使っている地区名。 */
  const usage = useMemo(() => {
    const map = new Map<string, string[]>();
    if (districtsState.status !== 'ready') return map;
    for (const d of districtsState.items) {
      const id = d.aiAnalysis?.streetSurveyTemplateId;
      if (!id) continue;
      map.set(id, [...(map.get(id) ?? []), d.name]);
    }
    return map;
  }, [districtsState]);
  const usageReady = districtsState.status === 'ready';

  const referenceSchema = useMemo(
    () => formats.find((f) => f.id === editing?.referenceOutputFormatId)?.schema ?? null,
    [formats, editing?.referenceOutputFormatId],
  );

  const extractionResult = editing ? extractionDefFromDraft(editing.extraction) : null;
  const idError =
    editing && isNew
      ? !editing.id.trim()
        ? 'ID を入力してください。'
        : !DOCUMENT_ID_PATTERN.test(editing.id.trim())
          ? 'ID は半角英小文字・数字をハイフンで区切って入力してください（例: walkable-desk-survey）。'
          : allItems.some((it) => it.id === editing.id.trim())
            ? '同じ ID のテンプレートがあります（アーカイブ済みを含みます）。'
            : null
      : null;
  const errors = [
    ...(editing && !editing.displayName.trim() ? ['表示名を入力してください。'] : []),
    ...(idError ? [idError] : []),
    ...(extractionResult?.errors ?? []),
  ];

  const previewCfg = useMemo(() => {
    if (!editing || !referenceSchema) return null;
    const { def } = extractionDefFromDraft(editing.extraction);
    return resolveStreetSurveyExtraction(referenceSchema, {
      extraction: { ...def, enabled: true },
    });
  }, [editing, referenceSchema]);

  async function handleImport(items: TemplateItem[]) {
    for (const it of items) {
      const existing = allItems.find((x) => x.id === it.id);
      const { id, ...rest } = stripSystemFields(it);
      await upsertMaster<StreetSurveyTemplate>({
        collectionName: STREET_SURVEY_TEMPLATES_COLLECTION,
        id: id as string,
        uid,
        isNew: !existing,
        data: rest as Omit<
          StreetSurveyTemplate,
          'id' | 'createdAt' | 'updatedAt' | 'createdBy' | 'updatedBy'
        >,
      });
    }
  }

  async function handleSave() {
    if (!editing || saving || errors.length > 0 || !extractionResult) return;
    setSaving(true);
    try {
      const display: StreetSurveyDisplayDef = {
        legendAxisKey: editing.axes.legendAxisKey ?? null,
        aggregateAxisKeys: editing.axes.aggregateAxisKeys ?? [],
        evaluationField: editing.axes.evaluationField ?? null,
        listFieldKeys: editing.listFieldKeys,
        extraction: extractionResult.def,
        result: resultFieldsDefFromDraft(editing.result),
      };
      const data = {
        displayName: editing.displayName.trim(),
        description: editing.description.trim() || null,
        referenceOutputFormatId: editing.referenceOutputFormatId || null,
        display,
        enabled: true,
      };
      await upsertMaster<StreetSurveyTemplate>({
        collectionName: STREET_SURVEY_TEMPLATES_COLLECTION,
        id: editing.id.trim(),
        uid,
        isNew,
        data: data as Omit<
          StreetSurveyTemplate,
          'id' | 'createdAt' | 'updatedAt' | 'createdBy' | 'updatedBy'
        >,
      });
      toast(
        'success',
        isNew
          ? 'テンプレートを作成しました。'
          : 'テンプレートを保存しました。使っている地区の次の解析から反映されます。',
      );
      setEditing(null);
    } catch (e) {
      toast('error', `保存に失敗しました: ${(e as Error).message}`);
    } finally {
      setSaving(false);
    }
  }

  async function handleArchive(item: TemplateItem) {
    const users = usage.get(item.id) ?? [];
    if (users.length > 0) {
      toast(
        'error',
        `「${item.displayName}」は ${users.join('、')} で使われているため、アーカイブできません。先に地区の机上調査ページでテンプレートを外してください。`,
      );
      return;
    }
    if (!confirm(`「${item.displayName}」をアーカイブしますか？`)) return;
    try {
      await archiveMaster(STREET_SURVEY_TEMPLATES_COLLECTION, item.id, uid);
      toast('success', 'アーカイブしました。');
    } catch (e) {
      toast('error', `アーカイブに失敗しました: ${(e as Error).message}`);
    }
  }

  function startDuplicate(item: TemplateItem) {
    const draft = draftFromItem(item);
    setEditing({ ...draft, id: '', displayName: `${item.displayName}（コピー）` });
    setIsNew(true);
  }

  return (
    <div>
      <header className="mb-6">
        <h1 className="text-2xl font-bold text-slate-900">机上調査テンプレート</h1>
        <p className="text-sm text-slate-500 mt-1">
          机上調査（ストリートビュー / 360
          度写真）の解析の設定と文面を、テンプレートとして地区に適用します。
          地区ごとの変更は、各地区の机上調査ページの「解析の設定」で上書きします（空欄の項目はテンプレートに従います）。
        </p>
      </header>

      <div className="mb-4 flex flex-wrap gap-2 items-center">
        <Button
          variant="primary"
          size="md"
          onClick={() => {
            setEditing(draftFromItem(null));
            setIsNew(true);
          }}
          disabled={editing !== null}
        >
          ＋ 新規作成
        </Button>
        <MasterImportExportButtons<TemplateItem>
          collectionName={STREET_SURVEY_TEMPLATES_COLLECTION}
          items={activeItems}
          validator={validateStreetSurveyTemplate}
          onImport={handleImport}
          disabled={editing !== null}
        />
      </div>

      {state.status === 'loading' && <p className="text-slate-500">読み込み中…</p>}
      {state.status === 'error' && (
        <p className="text-danger-600">読み込みに失敗しました: {state.error.message}</p>
      )}
      {state.status === 'ready' && !editing && (
        <ul className="grid grid-cols-1 gap-2 mb-6">
          {activeItems.length === 0 && (
            <li className="text-sm text-slate-500">テンプレートはまだありません。</li>
          )}
          {activeItems.map((item) => {
            const users = usage.get(item.id) ?? [];
            const enabled = item.display?.extraction?.enabled === true;
            return (
              <li key={item.id}>
                <Card padding="md" className="flex items-start justify-between gap-3 flex-wrap">
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <strong className="text-slate-900">{item.displayName}</strong>
                      <span className="text-xs text-slate-500">
                        {enabled ? '新方式を既定にする' : '従来の方式（地区で切り替え）'}
                      </span>
                    </div>
                    {item.description && (
                      <p className="mt-1 text-xs text-slate-600 whitespace-pre-wrap">
                        {item.description}
                      </p>
                    )}
                    <p className="mt-1 text-xs text-slate-500">
                      使っている地区:{' '}
                      {!usageReady ? '確認中…' : users.length > 0 ? users.join('、') : 'なし'}
                    </p>
                  </div>
                  <div className="flex gap-2 flex-wrap">
                    <RowExportButton
                      collectionName={STREET_SURVEY_TEMPLATES_COLLECTION}
                      item={item}
                    />
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={() => {
                        setEditing(draftFromItem(item));
                        setIsNew(false);
                      }}
                    >
                      編集
                    </Button>
                    <Button variant="secondary" size="sm" onClick={() => startDuplicate(item)}>
                      複製
                    </Button>
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={() => setHistoryTarget({ id: item.id, name: item.displayName })}
                    >
                      履歴
                    </Button>
                    <Button variant="danger" size="sm" onClick={() => void handleArchive(item)}>
                      アーカイブ
                    </Button>
                  </div>
                </Card>
              </li>
            );
          })}
        </ul>
      )}

      {historyTarget && (
        <MasterHistoryView
          collectionName={STREET_SURVEY_TEMPLATES_COLLECTION}
          masterId={historyTarget.id}
          masterDisplayName={historyTarget.name}
          onClose={() => setHistoryTarget(null)}
        />
      )}

      {editing && (
        <Card padding="lg" className="border-primary-300">
          <h2 className="text-lg font-bold mb-3">
            {isNew ? 'テンプレートの作成' : 'テンプレートの編集'}
          </h2>
          {!isNew && (usage.get(editing.id)?.length ?? 0) > 0 && (
            <p className="mb-3 rounded border border-amber-300 bg-amber-50 px-2 py-1 text-xs text-amber-800">
              このテンプレートは {usage.get(editing.id)!.join('、')}{' '}
              で使われています。保存すると、これらの地区の次の解析から変わります（解析済みの地点は再解析するまで古い設定の結果のままです）。
            </p>
          )}
          <div className="grid grid-cols-1 gap-4">
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <label className="block text-xs text-slate-600 min-w-0">
                表示名
                <input
                  className={inputClass}
                  value={editing.displayName}
                  onChange={(e) => setEditing({ ...editing, displayName: e.target.value })}
                  placeholder="例: ウォーカブル向け"
                />
              </label>
              <label className="block text-xs text-slate-600 min-w-0">
                ID（作成後は変更できません）
                <input
                  className={inputClass}
                  value={editing.id}
                  disabled={!isNew}
                  onChange={(e) => setEditing({ ...editing, id: e.target.value })}
                  placeholder="例: walkable-desk-survey"
                />
              </label>
            </div>
            <label className="block text-xs text-slate-600">
              説明（管理者向け）
              <textarea
                className={inputClass}
                rows={2}
                value={editing.description}
                onChange={(e) => setEditing({ ...editing, description: e.target.value })}
              />
            </label>
            <label className="block text-xs text-slate-600 min-w-0">
              項目の候補とプレビューに使う出力フォーマット
              <select
                className={inputClass}
                style={{ maxWidth: '100%' }}
                value={editing.referenceOutputFormatId}
                onChange={(e) =>
                  setEditing({ ...editing, referenceOutputFormatId: e.target.value })
                }
              >
                <option value="">選んでください</option>
                {formats.map((f) => (
                  <option key={f.id} value={f.id}>
                    {f.displayName}
                  </option>
                ))}
              </select>
              <span className="mt-0.5 block text-[11px] text-slate-400">
                解析では各地区の出力フォーマットを使います。ここで選んだ項目がその地区の出力フォーマットに無いときは、自動の既定に戻ります。
              </span>
            </label>

            <section className="grid grid-cols-1 gap-2 border-t border-slate-200 pt-3">
              <h3 className="text-sm font-semibold text-slate-800">調査項目の既定</h3>
              <StreetSurveyAxisDefaultsForm
                schema={referenceSchema}
                value={editing.axes}
                onChange={(axes) => setEditing({ ...editing, axes })}
              />
            </section>

            <section className="grid grid-cols-1 gap-2 border-t border-slate-200 pt-3">
              <h3 className="text-sm font-semibold text-slate-800">結果の表示に使う項目</h3>
              <StreetSurveyResultFieldsForm
                schema={referenceSchema}
                draft={editing.result}
                onChange={(result) => setEditing({ ...editing, result })}
                scope="template"
              />
            </section>

            <section className="grid grid-cols-1 gap-2 border-t border-slate-200 pt-3">
              <h3 className="text-sm font-semibold text-slate-800">解析の設定（新方式）と文面</h3>
              {referenceSchema ? (
                <StreetSurveyExtractionForm
                  schema={referenceSchema}
                  draft={editing.extraction}
                  onChange={(extraction) => setEditing({ ...editing, extraction })}
                  scope="template"
                />
              ) : (
                <p className="text-xs text-slate-400">
                  出力フォーマットを選ぶと、解析の設定と文面を編集できます。
                </p>
              )}
              {previewCfg && (
                <StreetSurveyPromptPreview cfg={previewCfg} mastersById={mastersById} />
              )}
            </section>

            {errors.length > 0 && (
              <ul className="list-disc pl-5 text-xs text-danger-700">
                {errors.map((e) => (
                  <li key={e}>{e}</li>
                ))}
              </ul>
            )}
            <div className="flex justify-end gap-2">
              <Button variant="secondary" size="md" onClick={() => setEditing(null)}>
                キャンセル
              </Button>
              <Button
                variant="primary"
                size="md"
                onClick={() => void handleSave()}
                disabled={saving || errors.length > 0}
              >
                {saving ? '保存中…' : '保存'}
              </Button>
            </div>
          </div>
        </Card>
      )}
    </div>
  );
}
